import type { ReactNode } from "react"
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { AdminTable, AdminTableCard, AdminTableCell, AdminTableHead, AdminTableRow } from '@/components/admin-table'
import { Skeleton } from '@/components/ui/skeleton'

type DataGridSortDirection = 'asc' | 'desc'

type DataGridSortState = {
  readonly column: string
  readonly direction: DataGridSortDirection
}

export type DataGridColumn<TData> = {
  readonly field?: keyof TData & string
  readonly colId?: string
  readonly headerName?: string
  readonly sortable?: boolean
  readonly render?: (row: TData) => ReactNode
}

type DataGridBaseProps<TData> = {
  /**
   * Height of the grid container (default: 500px)
   */
  height?: number | string
  columnDefs: DataGridColumn<TData>[]
  rowData?: TData[]
  loading?: boolean
  loadingRowCount?: number
  includeRowIndex?: boolean
  rowIndexMode?: 'ascending' | 'descending'
  rowIndexOffset?: number
  rowIndexTotal?: number | undefined
  onRowClicked?: (row: TData) => void
}

type DataGridSortingProps =
  | {
      sort: DataGridSortState
      onSortChange: (sort: DataGridSortState) => void
    }
  | {
      sort?: never
      onSortChange?: never
    }

export type DataGridProps<TData> = DataGridBaseProps<TData> & DataGridSortingProps

export function DataGrid<TData>({
  height = 500,
  columnDefs = [],
  rowData,
  loading,
  loadingRowCount = 6,
  includeRowIndex = true,
  rowIndexMode = 'ascending',
  rowIndexOffset,
  rowIndexTotal,
  onRowClicked,
  sort,
  onSortChange,
}: DataGridProps<TData>) {
  const gridHeight = typeof height === 'number' ? `${height}px` : height
  const safeRowData = Array.isArray(rowData) ? rowData : []
  const safeColumnDefs = Array.isArray(columnDefs) ? columnDefs : []
  const skeletonWidths = ['w-6', 'w-24', 'w-32', 'w-40', 'w-28', 'w-36', 'w-20']
  let resolvedOffset = 0
  if (typeof rowIndexOffset === 'number') {
    resolvedOffset = rowIndexOffset
  }
  let resolvedTotal: number | undefined = undefined
  if (typeof rowIndexTotal === 'number') {
    resolvedTotal = rowIndexTotal
  }

  const columns = safeColumnDefs
  const renderCell = (col: DataGridColumn<TData>, row: TData): ReactNode => {
    if (col.render !== undefined) return col.render(row)
    if (col.field === undefined) return null
    const value: unknown = row[col.field]
    if (value === null || value === undefined || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value
    throw new Error('DataGrid object values require a render callback')
  }

  const getColumnKey = (col: DataGridColumn<TData>, fallback: string) => {
    if (col.headerName) return col.headerName
    if (col.field) return col.field
    return fallback
  }
  const getSortColumn = (col: DataGridColumn<TData>): string | undefined => {
    if (col.sortable !== true || onSortChange === undefined) return undefined
    if (typeof col.colId === 'string' && col.colId.length > 0) return col.colId
    if (typeof col.field === 'string' && col.field.length > 0) return col.field
    throw new Error('Sortable DataGrid columns require a colId or field')
  }
  const visibleColumnCount = columns.length + (includeRowIndex ? 1 : 0)
  const getRowIndexDisplay = (index: number) => {
    if (rowIndexMode === 'descending') {
      const fallbackTotal = resolvedOffset + safeRowData.length
      const total = typeof resolvedTotal === 'number' ? resolvedTotal : fallbackTotal
      return total - resolvedOffset - index
    }
    return resolvedOffset + index + 1
  }

  return (
    <AdminTableCard style={{ minHeight: gridHeight }}>
      <div className="overflow-x-auto">
        <AdminTable>
          <thead>
            <tr className="bg-muted/40">
              {includeRowIndex ? <AdminTableHead>#</AdminTableHead> : null}
              {columns.map((col) => {
                const label = getColumnKey(col, '')
                const sortColumn = getSortColumn(col)
                const isActive = sortColumn !== undefined && sort !== undefined && sort.column === sortColumn
                const currentDirection = isActive ? sort.direction : undefined
                const nextDirection: DataGridSortDirection = currentDirection === 'asc' ? 'desc' : 'asc'
                const ariaSort = sortColumn === undefined
                  ? undefined
                  : currentDirection === 'asc'
                    ? 'ascending'
                    : currentDirection === 'desc'
                      ? 'descending'
                      : 'none'
                const SortIcon = currentDirection === 'asc'
                  ? ArrowUp
                  : currentDirection === 'desc'
                    ? ArrowDown
                    : ArrowUpDown

                return (
                  <AdminTableHead key={getColumnKey(col, 'col')} aria-sort={ariaSort}>
                    {sortColumn === undefined || onSortChange === undefined ? label : (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-sm text-left hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30"
                        aria-label={`Sort ${label} ${nextDirection === 'asc' ? 'ascending' : 'descending'}`}
                        onClick={() => onSortChange({ column: sortColumn, direction: nextDirection })}
                      >
                        <span>{label}</span>
                        <SortIcon className="size-3.5" aria-hidden="true" />
                      </button>
                    )}
                  </AdminTableHead>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              visibleColumnCount === 0 ? (
                <AdminTableRow className="pointer-events-none">
                  <AdminTableCell colSpan={1}>
                    <div className="py-6">
                      <Skeleton className="h-4 w-40" />
                    </div>
                  </AdminTableCell>
                </AdminTableRow>
              ) : (
                Array.from({ length: loadingRowCount }, (_, rowIndex) => (
                  <AdminTableRow key={`skeleton-${rowIndex}`} className="pointer-events-none">
                    {includeRowIndex ? (
                      <AdminTableCell key={`skeleton-${rowIndex}-index`}>
                        <Skeleton className={`h-4 ${skeletonWidths[0]}`} />
                      </AdminTableCell>
                    ) : null}
                    {columns.map((_, colIndex) => {
                      const widthIndex = (colIndex + (includeRowIndex ? 1 : 0)) % skeletonWidths.length
                      return (
                        <AdminTableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                          <Skeleton className={`h-4 ${skeletonWidths[widthIndex]}`} />
                        </AdminTableCell>
                      )
                    })}
                  </AdminTableRow>
                ))
              )
            ) : safeRowData.length === 0 ? (
              <AdminTableRow>
                <AdminTableCell colSpan={visibleColumnCount > 0 ? visibleColumnCount : 1}>
                  <div className="py-10 text-center text-muted-foreground">No records found</div>
                </AdminTableCell>
              </AdminTableRow>
            ) : (
              safeRowData.map((row, index) => (
                <AdminTableRow
                  key={index}
                  className={onRowClicked ? 'cursor-pointer' : undefined}
                  onClick={onRowClicked ? () => onRowClicked(row) : undefined}
                >
                  {includeRowIndex ? <AdminTableCell>{getRowIndexDisplay(index)}</AdminTableCell> : null}
                  {columns.map((col) => (
                    <AdminTableCell key={getColumnKey(col, 'cell')}>
                      {renderCell(col, row)}
                    </AdminTableCell>
                  ))}
                </AdminTableRow>
              ))
            )}
          </tbody>
        </AdminTable>
      </div>
    </AdminTableCard>
  )
}
